import React, { useEffect, useRef, useState } from 'react';
import { isCordova } from '~/utils/platform';

const CSS = require('./RequestPersonalizedOfferForm.module.scss');

interface TurnstileWidgetProps {
  onTokenChange: (token: string) => void;
  onError: (error: string | null) => void;
}

const getSiteKey = () => {
  if (process.env.NODE_ENV === 'development') {
    return '3x00000000000000000000FF';
  }
  return '0x4AAAAAACETm_NCGs7-gw6X';
};

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  onTokenChange,
  onError,
}) => {
  const SITE_KEY = getSiteKey();
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const hiddenTokenInputRef = useRef<HTMLInputElement | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [turnstileReady, setTurnstileReady] = useState<boolean>(false);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isCordova()) return;
    if (!SITE_KEY) {
      console.warn('Turnstile site key not configured');
      return;
    }

    const w = window as any;
    if (w.turnstile) {
      setTurnstileReady(true);
      return;
    }

    const existing = document.querySelector('script[data-turnstile="true"]');
    if (existing) {
      if (w.turnstile) {
        setTurnstileReady(true);
      } else {
        existing.addEventListener('load', () => setTurnstileReady(true));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-turnstile', 'true');
    script.onload = () => setTurnstileReady(true);
    script.onerror = () => {
      const errorMsg =
        'Security check failed to load. Please refresh and try again.';
      setTurnstileError(errorMsg);
      onError(errorMsg);
    };
    document.head.appendChild(script);
  }, [SITE_KEY, onError]);

  useEffect(() => {
    if (isCordova()) return undefined;
    if (!turnstileReady) return undefined;
    if (!SITE_KEY) return undefined;
    const w = window as any;
    if (!w.turnstile || !turnstileContainerRef.current) return undefined;

    try {
      widgetIdRef.current = w.turnstile.render(turnstileContainerRef.current, {
        sitekey: SITE_KEY,
        callback: (token: string) => {
          setTurnstileToken(token);
          hiddenTokenInputRef.current &&
            (hiddenTokenInputRef.current.value = token);
          setTurnstileError(null);
          onTokenChange(token);
          onError(null);
        },
        'error-callback': () => {
          setTurnstileToken('');
          if (hiddenTokenInputRef.current)
            hiddenTokenInputRef.current.value = '';
          const errorMsg = 'Security check failed. Please try again.';
          setTurnstileError(errorMsg);
          onTokenChange('');
          onError(errorMsg);
        },
        'expired-callback': () => {
          setTurnstileToken('');
          if (hiddenTokenInputRef.current)
            hiddenTokenInputRef.current.value = '';
          const errorMsg = 'Security check expired. Please complete it again.';
          setTurnstileError(errorMsg);
          onTokenChange('');
          onError(errorMsg);
          try {
            w.turnstile &&
              widgetIdRef.current &&
              w.turnstile.reset(widgetIdRef.current);
          } catch (e) {
            // ignore
          }
        },
        theme: 'auto',
        size: 'flexible',
      });
    } catch (e) {
      const errorMsg = 'Security check failed to initialize.';
      setTurnstileError(errorMsg);
      onError(errorMsg);
    }

    return () => {
      try {
        if (widgetIdRef.current && w.turnstile) {
          w.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      } catch (e) {
        // ignore
      }
    };
  }, [turnstileReady, SITE_KEY, onTokenChange, onError]);

  if (isCordova()) {
    return null;
  }

  return (
    <div className={CSS.TurnstileContainer}>
      <div ref={turnstileContainerRef} />
      {/* Hidden input to pass token into handleSubmit via FormData */}
      <input
        type="hidden"
        name="turnstile_token"
        ref={hiddenTokenInputRef}
        value={turnstileToken}
        readOnly
      />
      {turnstileError && (
        <div className={CSS.TurnstileError} role="alert">
          {turnstileError}
        </div>
      )}
    </div>
  );
};

export const useTurnstile = () => {
  const SITE_KEY = getSiteKey();
  const isRequired = !isCordova() && !!SITE_KEY;

  return {
    siteKey: SITE_KEY,
    isRequired,
  };
};

export default TurnstileWidget;
